import React from 'react';
import ReactMarkdown from 'react-markdown';
import { Badge } from '../Badge/Badge';
import { Image } from '../Image/Image';
import { CustomIcon } from '../CustomIcon/CustomIcon';
import './NsiCard.scss';

/**
 * NsiCard - New Selection Incentives card component
 *
 * @deprecated Use `CardGrid` (with the default `mediaStyle="image"`) for new
 * content. `NsiCard` is kept for backward compatibility with existing PolyC
 * pages and will render indefinitely, but new NSI content should register as
 * `CardGrid`.
 * 
 * Displays NSI program information with image, badge, and highlighted benefits list.
 *
 * @polyDeprecated true
 * @polySemanticUsage Deprecated: do not select this block for new content; existing pages keep
 * rendering.
 */

export interface NsiCardProps {
  /**
   * Image URL
   * @polyType Url
   */
  imageSrc?: string;
  
  /**
   * Image alt text
   */
  imageAlt?: string;
  
  /** Badge text */
  badgeText?: string;
  
  /**
   * Card heading
   */
  heading: string;
  
  /** Card subheading */
  subheading?: string;
  
  /**
   * List of benefits (displayed with green highlights)
   */
  benefits?: string[];
  
  /** Link text */
  linkText?: string;
  
  /**
   * Link href
   * @polyType Url
   */
  linkHref?: string;
  
  // Overlay 1 props
  /**
   * Overlay 1 image source URL (positioned on top of main image)
   * @polyType Url
   */
  overlay1Url?: string;
  /**
   * Overlay 1 image alt text
   */
  overlay1Alt?: string;
  /**
   * Overlay 1 image position
   * @polyOptions Bottom Right=bottom-right,Bottom Left=bottom-left,Top Right=top-right,Top Left=top-left,Center=center
   */
  overlay1Position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left' | 'center';
  /**
   * Overlay 1 image width in pixels, e.g. '240px'. Leave unset to show the image at its designed size
   * @polyHelp Width in pixels, e.g. 240px. Leave blank to show the image at its designed size.
   * @polyTranslatable false
   */
  overlay1Width?: string;

  // Overlay 2 props
  /**
   * Overlay 2 image source URL (positioned on top of main image)
   * @polyType Url
   */
  overlay2Url?: string;
  /**
   * Overlay 2 image alt text
   */
  overlay2Alt?: string;
  /**
   * Overlay 2 image position
   * @polyOptions Bottom Right=bottom-right,Bottom Left=bottom-left,Top Right=top-right,Top Left=top-left,Center=center
   */
  overlay2Position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left' | 'center';
  /**
   * Overlay 2 image width in pixels, e.g. '240px'. Leave unset to show the image at its designed size
   * @polyHelp Width in pixels, e.g. 240px. Leave blank to show the image at its designed size.
   * @polyTranslatable false
   */
  overlay2Width?: string;
}

export const NsiCard: React.FC<NsiCardProps> = ({
  imageSrc,
  imageAlt,
  badgeText,
  heading,
  subheading,
  benefits,
  linkText = 'See all incentives',
  linkHref = '#',
  overlay1Url,
  overlay1Alt,
  overlay1Position,
  overlay1Width,
  overlay2Url,
  overlay2Alt,
  overlay2Position,
  overlay2Width,
}) => {
  // Handle empty string or undefined for badgeText
  const displayBadgeText = badgeText || 'New Seller Incentives';
  
  return (
    <section className="nsi-card">
      {imageSrc && (
        <div className="nsi-card__image-column">
          <Image 
            url={imageSrc} 
            alt={imageAlt ?? ''}
            objectFit="cover"
            overlay1Url={overlay1Url}
            overlay1Alt={overlay1Alt}
            overlay1Position={overlay1Position}
            overlay1Width={overlay1Width}
            overlay2Url={overlay2Url}
            overlay2Alt={overlay2Alt}
            overlay2Position={overlay2Position}
            overlay2Width={overlay2Width}
          />
        </div>
      )}
      
      <div className="nsi-card__content">
        <Badge size="md" tone="nsi" icon="star" className="nsi-card__badge">
          {displayBadgeText}
        </Badge>
        
        <h2 className="nsi-card__heading">
          {heading}
        </h2>
        
        {subheading && (
          <p className="nsi-card__subheading">{subheading}</p>
        )}
        
        {benefits && benefits.length > 0 && (
          <div className="nsi-card__benefits-container">
            {benefits.map((benefit, index) => (
              <div key={index} className="nsi-card__benefit-item">
                <CustomIcon name="check" size={20} aria-hidden="true" />
                <ReactMarkdown>{benefit}</ReactMarkdown>
              </div>
            ))}
          </div>
        )}
        
        {linkText && linkHref && (
          <a href={linkHref} className="nsi-card__link link-with-arrow">
            {linkText}
          </a>
        )}
      </div>
    </section>
  );
};
